import { createBrowserRouter, Navigate } from 'react-router'

import { LoginPage, PERMISSIONS, RequiredPasswordPage } from '@/features/auth'

import { AppLayout } from '../layouts/AppLayout'
import { ForbiddenPage } from '../pages/ForbiddenPage'
import { NotFoundPage } from '../pages/NotFoundPage'
import { AuthenticatedRootRedirect } from './AuthenticatedRootRedirect'
import { ProtectedRoute } from './ProtectedRoute'
import { ROUTES } from './routes'

/**
 * =============================================================================
 * DEFINICIÓN DE RUTAS
 * =============================================================================
 * Mapa completo de la aplicación. Al leerlo de arriba abajo se entiende toda
 * la navegación del sistema.
 *
 * Sobre `lazy`: cada página se carga en un archivo JavaScript aparte, y solo
 * cuando el usuario entra a esa ruta. Esto se llama "code splitting". Sin él,
 * abrir el login descargaría también el dashboard, el inventario y todo lo
 * demás — y la primera carga sería cada vez más lenta a medida que el proyecto
 * crece. Con él, el arranque se mantiene rápido para siempre.
 *
 * Fíjate además en que cada ruta declara su permiso: la autorización es parte
 * de la definición de la ruta, no algo que cada página tenga que recordar
 * comprobar por su cuenta. Es imposible olvidarlo.
 * =============================================================================
 */
export const router = createBrowserRouter([
  {
    path: ROUTES.LOGIN,
    element: <LoginPage />,
  },
  {
    path: ROUTES.FORBIDDEN,
    element: <ForbiddenPage />,
  },
  {
    path: ROUTES.REQUIRED_PASSWORD,
    element: (
      <ProtectedRoute>
        <RequiredPasswordPage />
      </ProtectedRoute>
    ),
  },
  {
    // Ruta "padre": todo lo de adentro comparte el AppLayout (barra lateral y
    // encabezado) y exige sesión iniciada.
    path: ROUTES.ROOT,
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <AuthenticatedRootRedirect />,
      },
      {
        path: ROUTES.DASHBOARD,
        lazy: async () => {
          const { DashboardPage } = await import('@/features/dashboard')
          return {
            element: (
              <ProtectedRoute permission={PERMISSIONS.DASHBOARD_VIEW}>
                <DashboardPage />
              </ProtectedRoute>
            ),
          }
        },
      },
      {
        path: ROUTES.CUSTOMERS,
        lazy: async () => {
          const { CustomersPage } = await import('@/features/customers')
          return {
            element: (
              <ProtectedRoute permission={PERMISSIONS.CUSTOMERS_VIEW}>
                <CustomersPage />
              </ProtectedRoute>
            ),
          }
        },
      },
      {
        path: ROUTES.CUSTOMER_DETAIL,
        lazy: async () => {
          const { CustomerDetailPage } = await import('@/features/customers')
          return {
            element: (
              <ProtectedRoute permission={PERMISSIONS.CUSTOMERS_VIEW}>
                <CustomerDetailPage />
              </ProtectedRoute>
            ),
          }
        },
      },
      {
        path: ROUTES.TRIP_SALES,
        lazy: async () => {
          const { TripSalesPage } = await import('@/features/trip-sales')
          return {
            element: (
              <ProtectedRoute permission={PERMISSIONS.TRIP_SALES_VIEW}>
                <TripSalesPage />
              </ProtectedRoute>
            ),
          }
        },
      },
      {
        path: ROUTES.INDIVIDUAL_TRIPS,
        lazy: async () => {
          const { IndividualTripsPage } = await import('@/features/trip-sales')
          return {
            element: (
              <ProtectedRoute permission={PERMISSIONS.INDIVIDUAL_TRIPS_VIEW}>
                <IndividualTripsPage />
              </ProtectedRoute>
            ),
          }
        },
      },
      {
        path: ROUTES.INDIVIDUAL_TRIPS_REGISTER,
        lazy: async () => {
          const { RegisterIndividualTripPage } = await import('@/features/trip-sales')
          return {
            element: (
              <ProtectedRoute permission={PERMISSIONS.INDIVIDUAL_TRIPS_CREATE}>
                <RegisterIndividualTripPage />
              </ProtectedRoute>
            ),
          }
        },
      },
      {
        path: ROUTES.INDIVIDUAL_TRIP_DETAIL,
        lazy: async () => {
          const { IndividualTripDetailPage } = await import('@/features/trip-sales')
          return {
            element: (
              <ProtectedRoute permission={PERMISSIONS.INDIVIDUAL_TRIPS_VIEW}>
                <IndividualTripDetailPage />
              </ProtectedRoute>
            ),
          }
        },
      },
      {
        path: ROUTES.TRIP_SALE_DETAIL,
        lazy: async () => {
          const { TripSaleDetailPage } = await import('@/features/trip-sales')
          return {
            element: (
              <ProtectedRoute permission={PERMISSIONS.TRIP_SALES_VIEW}>
                <TripSaleDetailPage />
              </ProtectedRoute>
            ),
          }
        },
      },
      {
        path: ROUTES.GROUP_TRIPS,
        lazy: async () => {
          const { GroupTripsPage } = await import('@/features/group-trips')
          return {
            element: (
              <ProtectedRoute permission={PERMISSIONS.GROUP_TRIPS_VIEW}>
                <GroupTripsPage />
              </ProtectedRoute>
            ),
          }
        },
      },
      {
        path: ROUTES.GROUP_TRIP_DETAIL,
        lazy: async () => {
          const { GroupTripDetailPage } = await import('@/features/group-trips')
          return {
            element: (
              <ProtectedRoute permission={PERMISSIONS.GROUP_TRIPS_VIEW}>
                <GroupTripDetailPage />
              </ProtectedRoute>
            ),
          }
        },
      },
      {
        path: ROUTES.ABONOS,
        lazy: async () => {
          const { PaymentsPage } = await import('@/features/payments')
          return {
            element: (
              <ProtectedRoute permission={PERMISSIONS.PAYMENTS_VIEW}>
                <PaymentsPage />
              </ProtectedRoute>
            ),
          }
        },
      },
      {
        path: ROUTES.PAYMENTS,
        element: (
          <ProtectedRoute permission={PERMISSIONS.PAYMENTS_VIEW}>
            <Navigate to={ROUTES.ABONOS} replace />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.OPERATORS,
        lazy: async () => {
          const { OperatorsPage } = await import('@/features/operators')
          return {
            element: (
              <ProtectedRoute permission={PERMISSIONS.OPERATORS_VIEW}>
                <OperatorsPage />
              </ProtectedRoute>
            ),
          }
        },
      },
      {
        path: ROUTES.OPERATOR_DETAIL,
        lazy: async () => {
          const { OperatorDetailPage } = await import('@/features/operators')
          return {
            element: (
              <ProtectedRoute permission={PERMISSIONS.OPERATORS_VIEW}>
                <OperatorDetailPage />
              </ProtectedRoute>
            ),
          }
        },
      },
      {
        path: ROUTES.INVENTORY,
        lazy: async () => {
          const { InventoryPage } = await import('@/features/inventory')
          return {
            element: (
              <ProtectedRoute permission={PERMISSIONS.INVENTORY_VIEW}>
                <InventoryPage />
              </ProtectedRoute>
            ),
          }
        },
      },
      {
        path: ROUTES.PRODUCTS,
        lazy: async () => {
          const { ProductsPage } = await import('@/features/products')
          return {
            element: (
              <ProtectedRoute permission={PERMISSIONS.PRODUCTS_VIEW}>
                <ProductsPage />
              </ProtectedRoute>
            ),
          }
        },
      },
      {
        path: ROUTES.PRODUCT_SALES,
        lazy: async () => {
          const { ProductSalesPage } = await import('@/features/product-sales')
          return {
            element: (
              <ProtectedRoute permission={PERMISSIONS.PRODUCT_SALES_VIEW}>
                <ProductSalesPage />
              </ProtectedRoute>
            ),
          }
        },
      },
      {
        path: ROUTES.PRODUCT_SALE_DETAIL,
        lazy: async () => {
          const { ProductSaleDetailPage } = await import('@/features/product-sales')
          return {
            element: (
              <ProtectedRoute permission={PERMISSIONS.PRODUCT_SALES_VIEW}>
                <ProductSaleDetailPage />
              </ProtectedRoute>
            ),
          }
        },
      },
      {
        path: ROUTES.PRODUCT_ABONOS,
        lazy: async () => {
          const { ProductAbonosPage } = await import('@/features/product-sales')
          return {
            element: (
              <ProtectedRoute permission={PERMISSIONS.PRODUCT_SALES_VIEW}>
                <ProductAbonosPage />
              </ProtectedRoute>
            ),
          }
        },
      },
      {
        path: ROUTES.CASH_REGISTER,
        lazy: async () => {
          const { CashRegisterPage } = await import('@/features/cash-register')
          return {
            element: (
              <ProtectedRoute permission={PERMISSIONS.CASH_REGISTER_VIEW}>
                <CashRegisterPage />
              </ProtectedRoute>
            ),
          }
        },
      },
      {
        path: ROUTES.SELLERS,
        lazy: async () => {
          const { SellersPage } = await import('@/features/user-seller-administration')
          return {
            element: (
              <ProtectedRoute permission={PERMISSIONS.USERS_MANAGE}>
                <SellersPage />
              </ProtectedRoute>
            ),
          }
        },
      },
      {
        path: ROUTES.USERS,
        lazy: async () => {
          const { UsersPage } = await import('@/features/user-seller-administration')
          return {
            element: (
              <ProtectedRoute permission={PERMISSIONS.USERS_MANAGE}>
                <UsersPage />
              </ProtectedRoute>
            ),
          }
        },
      },
      {
        path: ROUTES.ADMIN_USERS_SELLERS,
        element: (
          <ProtectedRoute permission={PERMISSIONS.USERS_MANAGE}>
            <Navigate to={ROUTES.SELLERS} replace />
          </ProtectedRoute>
        ),
      },
    ],
  },
  {
    // Comodín: cualquier URL que no coincida con las de arriba.
    path: '*',
    element: <NotFoundPage />,
  },
])
